import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserModel } from '../user.model';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})


export class AuthService {
  private baseUrl = environment.apiBaseUrl + '/account'; // Ihr Backend-URL
  private accessUrl = environment.apiBaseUrl + '/access'; // Zugriffssteuerung
  private token: string | null = null;
  private jwtHelper = new JwtHelperService();
  private logoutTimer: any;
  private readonly ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';


  constructor(private http: HttpClient, private router: Router, private snackBar: MatSnackBar) { }

  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/login`, { email, password })
      .pipe(
        map((response: any) => {
          this.token = response.token;
          if (this.token) {
            const decodedToken = this.jwtHelper.decodeToken(this.token);
            const expiresAt = decodedToken.exp * 1000;
            localStorage.setItem('token', this.token);
            localStorage.setItem('tokenExpiry', expiresAt.toString()); // ← Echte Ablaufzeit

            if (response.refreshToken) {
              localStorage.setItem('refreshToken', response.refreshToken);
            }

            // Refresh 2 Minuten vor Ablauf
            const refreshDelay = Math.max(0, expiresAt - Date.now() - 120000);
            this.setLogoutTimer(refreshDelay);
          }
          return response;
        }),
        catchError(this.handleError)
      );
  }

// Gleiche Änderung in refreshToken():



  refreshToken(): Observable<any> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      this.logout();
      return throwError('Kein Refresh Token vorhanden.');
    }
    return this.http.post(`${this.baseUrl}/refresh-token`, { refreshToken })
      .pipe(
        map((response: any) => {
          this.token = response.token;
          if (this.token) {
            const decodedToken = this.jwtHelper.decodeToken(this.token);
            const expiresAt = decodedToken.exp * 1000;
            localStorage.setItem('token', this.token);
            localStorage.setItem('tokenExpiry', expiresAt.toString());
            if (response.refreshToken) {
              localStorage.setItem('refreshToken', response.refreshToken);
            }
            const refreshDelay = Math.max(0, expiresAt - Date.now() - 120000);
            this.setLogoutTimer(refreshDelay);
          }
          return response;
        }),
        catchError(err => {
          this.logout();
          return throwError(err);
        })
      );
  }

  private setLogoutTimer(duration: number) {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
    }

    if (duration <= 0) {
      this.refreshToken().subscribe({
        next: () => { },
        error: () => this.logout()
      });
      return;
    }

    this.logoutTimer = setTimeout(() => {
      this.refreshToken().subscribe({
        next: () => { },
        error: () => this.logout()
      });

    }, duration);
  }

  private clearLogoutTimer() {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  register(user: UserModel): Observable<any> {
    const token = this.getToken();
    if (!token) {
      return throwError('Keine Berechtigung für die Benutzerverwaltung.');
    }
    return this.http.post(`${this.baseUrl}/register`, user, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  updateUser(user: UserModel): Observable<any> {
    const token = this.getToken();
    return this.http.put(`${this.baseUrl}/update/${user.id}`, user, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  deleteUser(userId: string): Observable<any> {
    const token = this.getToken();
    return this.http.delete(`${this.baseUrl}/delete/${userId}`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  getUsers(): Observable<UserModel[]> {
    const token = this.getToken();
    return this.http.get<UserModel[]>(`${this.baseUrl}/users`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  getCurrentUser(): Observable<UserModel> {
    const token = this.getToken();
    return this.http.get<UserModel>(`${this.baseUrl}/currentuser`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  resetPassword(userId: string): Observable<any> {
    const token = this.getToken();
    return this.http.put(`${this.baseUrl}/resetpassword/${userId}`, {}, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/forgot-password`, { email }).pipe(
      catchError(this.handleError)
    );
  }

  changePassword(oldPassword: string, newPassword: string): Observable<any> {
    const token = this.getToken();
    return this.http.put(`${this.baseUrl}/changepassword`, { oldPassword, newPassword }, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const expiry = localStorage.getItem('tokenExpiry');
    if (token && expiry) {
      const now = Date.now();
      if (now > parseInt(expiry)) {
        // Token ist wirklich abgelaufen
        this.logout();
        return false;
      }
      // Timer nur setzen wenn noch nicht aktiv
      if (!this.logoutTimer) {
        const refreshDelay = Math.max(0, parseInt(expiry) - now - 120000);
        this.setLogoutTimer(refreshDelay);
      }
      return !this.jwtHelper.isTokenExpired(token);
    }
    return false;
  }

  /** Alle Rollen/Gruppen des Benutzers als Array (das JWT-Claim kann String oder Array sein). */
  getRoles(): string[] {
    const token = this.getToken();
    if (!token || !this.isAuthenticated()) {
      return [];
    }
    const decodedToken = this.jwtHelper.decodeToken(token);
    const role = decodedToken?.[this.ROLE_CLAIM];
    if (!role) {
      return [];
    }
    return Array.isArray(role) ? role : [role];
  }

  hasRole(role: string): boolean {
    return this.getRoles().includes(role);
  }

  isSuperAdmin(): boolean {
    return this.hasRole('SuperAdmin');
  }

  isAdmin(): boolean {
    // SuperAdmin ist eine Obermenge von Admin.
    return this.hasRole('Admin') || this.isSuperAdmin();
  }

  isCommercial(): boolean {
    return this.hasRole('Kaufmaennisch');
  }

  /** Ressourcenplanung sehen nur SuperAdmin und die kaufmännische Gruppe. */
  canSeeResourcePlanning(): boolean {
    return this.isSuperAdmin() || this.isCommercial();
  }

  /** Rollen/SuperAdmin vergeben darf nur der SuperAdmin. */
  canManageRoles(): boolean {
    return this.isSuperAdmin();
  }

  isBauleiter(): boolean {
    return this.hasRole('Bauleiter');
  }

  isMontage(): boolean {
    return this.hasRole('Montage');
  }

  isVorarbeiter(): boolean {
    return this.hasRole('Vorarbeiter');
  }

  isProjektleiterKaufmann(): boolean {
    return this.hasRole('ProjektleiterKaufmann');
  }

  /**
   * Preise/Stundensätze (Euro) SEHEN. Jede Management-/Bauleiter-/Kaufmann-Rolle darf das –
   * der Vorarbeiter bewusst NICHT. (Server entfernt Preise zusätzlich hart aus den Daten.)
   */
  canSeePrices(): boolean {
    return this.isSuperAdmin() || this.isAdmin() || this.isCommercial()
      || this.isProjektleiterKaufmann() || this.isBauleiter();
  }

  /** Stammkunden anlegen/verwalten darf nur der SuperAdmin. */
  canManageStammkunden(): boolean {
    return this.isSuperAdmin();
  }

  /**
   * "Reiner" Vorarbeiter: nur die Feld-Rolle Vorarbeiter, keine Leitungs-/Büro-Rolle.
   * Dieser sieht bewusst NUR das Lager und das Ticketsystem ("Meine Einsätze") –
   * keine Stammkunden-Verwaltung, keine Wartung, keine Planung.
   */
  isFieldVorarbeiter(): boolean {
    return this.isVorarbeiter()
      && !this.isSuperAdmin() && !this.isAdmin() && !this.isCommercial()
      && !this.isProjektleiterKaufmann() && !this.isBauleiter();
  }

  /** Wer darf den Stammkunden-Verwaltungsbereich betreten (Sichtbarkeit wird serverseitig gescopt). */
  canAccessStammkunden(): boolean {
    // Der reine Vorarbeiter arbeitet nur über das Ticketsystem, nicht in der Verwaltung.
    if (this.isFieldVorarbeiter()) {
      return false;
    }
    return this.isSuperAdmin() || this.isAdmin() || this.isCommercial()
      || this.isProjektleiterKaufmann() || this.isBauleiter() || this.isVorarbeiter();
  }

  /** Die persönliche Feld-Sicht "Meine Einsätze" richtet sich an die Personen vor Ort. */
  canSeeMeineEinsaetze(): boolean {
    return this.isVorarbeiter() || this.isBauleiter() || this.isMontage();
  }

  /** Die Projekt-Gesamtübersicht (Freigaben, Auswertungen) ist für Bauleiter/Admin. */
  canSeeBauleiterUebersicht(): boolean {
    return this.isBauleiter() || this.isAdmin() || this.isSuperAdmin();
  }

  /** Montage und Vorarbeiter dürfen nur lesen. Alle anderen Gruppen (inkl. Legacy "user") dürfen bearbeiten. */
  canEdit(): boolean {
    const roles = this.getRoles();
    const isRestricted = roles.includes('Montage') || roles.includes('Vorarbeiter');
    const isEditor = roles.includes('SuperAdmin') || roles.includes('Admin')
      || roles.includes('Kaufmaennisch') || roles.includes('Bauleiter')
      || roles.includes('ProjektleiterKaufmann');
    return !(isRestricted && !isEditor);
  }

  canManageUsers(): boolean {
    return this.isAdmin();
  }

  // ---- Zugriffssteuerung (Lager/Kunden-Zuordnung) ----

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({ 'Authorization': `Bearer ${this.getToken()}` });
  }

  getMyPermissions(): Observable<any> {
    return this.http.get(`${this.accessUrl}/me`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getGroups(): Observable<string[]> {
    return this.http.get<string[]>(`${this.accessUrl}/groups`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getWarehouses(): Observable<{ id: number; name: string }[]> {
    return this.http.get<{ id: number; name: string }[]>(`${this.accessUrl}/warehouses`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getUserAccess(userId: string): Observable<{ userId: string; warehouseIds: number[]; customerIds: number[] }> {
    return this.http.get<{ userId: string; warehouseIds: number[]; customerIds: number[] }>(
      `${this.accessUrl}/users/${userId}`, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  setUserAccess(userId: string, warehouseIds: number[], customerIds: number[]): Observable<any> {
    return this.http.put(`${this.accessUrl}/users/${userId}`,
      { userId, warehouseIds, customerIds }, { headers: this.authHeaders() })
      .pipe(catchError(this.handleError));
  }

  getAssignableCustomers(): Observable<{ id: number; name: string }[]> {
    return this.http.get<any[]>(`${environment.apiBaseUrl}/customer`, { headers: this.authHeaders() })
      .pipe(
        map(list => (list || []).map(c => ({ id: c.id, name: c.name }))),
        catchError(this.handleError)
      );
  }

  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('refreshToken');
    this.token = null;
    this.clearLogoutTimer();
    this.router.navigate(['/home']);
  }

  getUserActivities(): Observable<any> {
    const token = this.getToken();
    return this.http.get(`${this.baseUrl}/activities`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  getSignature(): Observable<{ hasSignature: boolean; url: string | null }> {
    const token = this.getToken();
    return this.http.get<{ hasSignature: boolean; url: string | null }>(`${this.baseUrl}/signature`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  uploadSignature(dataUrl: string): Observable<{ hasSignature: boolean; url: string }> {
    const token = this.getToken();
    return this.http.post<{ hasSignature: boolean; url: string }>(`${this.baseUrl}/signature`, { dataUrl }, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  deleteSignature(): Observable<{ hasSignature: boolean }> {
    const token = this.getToken();
    return this.http.delete<{ hasSignature: boolean }>(`${this.baseUrl}/signature`, {
      headers: new HttpHeaders({ 'Authorization': `Bearer ${token}` })
    }).pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Es ist ein unbekannter Fehler aufgetreten.';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.message;
    } else if (typeof error.error === 'string' && error.error.trim()) {
      errorMessage = error.error;
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (Array.isArray(error.error)) {
      errorMessage = error.error
        .map((err: any) => err?.description || err)
        .join(' ');
    } else if (error.error?.errors) {
      const validationMessages = Object.values(error.error.errors as Record<string, string[]>)
        .flat()
        .filter(Boolean)
        .join(' ');
      if (validationMessages) {
        errorMessage = validationMessages;
      }
    } else if (error.status) {
      const statusText = error.statusText || error.message || 'Unbekannter Fehler';
      errorMessage = `Fehler (${error.status}): ${statusText}`;
    }

    const enrichedError = new HttpErrorResponse({
      error: errorMessage,
      headers: error.headers,
      status: error.status,
      statusText: error.statusText ?? '',
      url: error.url ?? undefined  // WICHTIG: null -> undefined
    });

    return throwError(enrichedError);
  }
}
